/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {useContext, useEffect, useRef, useState} from "react";
import '../../css/style.css';
import Button from "@components/Button";
import ConfirmAlert from "@components/ConfirmAlert";
import NavigationButton from "@components/NavigationButton";
import {Toaster} from "react-hot-toast";
import ErrorAlert from "@components/ErrorAlert";
import {SetAlertOnContext} from "@components/page/AppHome";
import {t} from "@util/i18n";

interface ShowSelectedProps {
    selectedImages: File[];
    openFileFunc: () => void;
    setSent: () => void;
    deleteFunc: (index: number) => void;
    imageToBig: boolean;
    setImageToBig: (status: boolean) => void;
}

const trashIcon = require("../../res/images/trash.png");
const arrowIcon = require("../../res/images/arrow.png");
const arrowDisabledIcon = require("../../res/images/arrow_disabled.png");

function ShowSelected({
                          selectedImages,
                          openFileFunc,
                          setSent,
                          deleteFunc,
                          setImageToBig,
                          imageToBig
                      }: ShowSelectedProps) {
    const {setAlertOn} = useContext(SetAlertOnContext);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAlertOpen, setAlertOpen] = useState(false);
    const touchStartX = useRef(0);
    const [webPictureScrolling, setWebPictureScrolling] = useState(false);
    const [webStartGalleryIndex, setWebStartGalleryIndex] = useState(0);
    const [webEndGalleryIndex, setWebEndGalleryIndex] = useState(5);
    const [selectedDeletedIndex, setSelectedDeletedIndex] = useState(0);
    const [maxImagesAlert, setMaxImagesAlert] = useState(false)

    const isMobile = window.innerWidth <= 768;
    const alertTitle = t("deletePhotoTitle");
    const alertText = t("deletePhotoText");

    useEffect(() => {
        if (selectedImages.length > 5) {
            setWebPictureScrolling(true);
        } else {
            setWebPictureScrolling(false);
        }
    }, [selectedImages]);

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        const touchEndX = e.changedTouches[0].clientX;
        const deltaX = touchEndX - touchStartX.current;

        if (deltaX > 50) {
            // Swipe right
            prevImage();
        } else if (deltaX < -50) {
            // Swipe left
            nextImage();
        }
    };

    const nextImage = () => {
        setCurrentIndex((imageIndex) =>
            imageIndex < selectedImages.length - 1 ? imageIndex + 1 : imageIndex);
    };

    const nextImageWeb = () => {
        if (webEndGalleryIndex >= selectedImages.length)
            return;

        const nextStartIndex = webStartGalleryIndex + 1;
        const nextEndIndex = webEndGalleryIndex + 1;

        setWebStartGalleryIndex(nextStartIndex);
        setWebEndGalleryIndex(nextEndIndex);
    };

    const prevImageWeb = () => {
        if (webStartGalleryIndex === 0)
            return;

        const prevStartIndex = webStartGalleryIndex - 1;
        const prevEndIndex = webEndGalleryIndex - 1;

        setWebStartGalleryIndex(prevStartIndex);
        setWebEndGalleryIndex(prevEndIndex);
    };

    const prevImage = () => {
        setCurrentIndex((imageIndex) =>
            imageIndex > 0 ? imageIndex - 1 : imageIndex);
    };

    const getPrevArrow = () => {
        return currentIndex === 0 ? arrowDisabledIcon : arrowIcon;
    };

    const getNextArrow = () => {
        return currentIndex === selectedImages.length - 1 ? arrowDisabledIcon : arrowIcon;
    };

    const getNextArrowWeb = () => {
        return webEndGalleryIndex < selectedImages.length ? arrowIcon : arrowDisabledIcon;
    };

    const getPrevArrowWeb = () => {
        return webStartGalleryIndex > 0 ? arrowIcon : arrowDisabledIcon;
    };


    const handleSendImages = () => {
        setSent();
    };

    const handleConfirmError = () => {
        setImageToBig(false);
        setAlertOn(false)
    };

    const handleImageMaxAlert = () => {
        setMaxImagesAlert(false)
        setAlertOn(false)
    }


    const handleTrashClick = () => {
        setAlertOpen(true);
        setAlertOn(true)
    };

    const webHandleTrashClick = (index: number) => {
        setAlertOpen(true);
        setSelectedDeletedIndex(index);
        setAlertOn(true)
    };
    /*
    * Confirm alert functions
    * */
    const handleConfirm = () => {
        setAlertOpen(false);
        setAlertOn(false)
        deleteFunc(currentIndex);
        setCurrentIndex(currentIndex === 0 ? 0 : currentIndex - 1);
    };


    const handleConfirmWeb = (index) => {
        setAlertOpen(false);
        setAlertOn(false)
        deleteFunc(index);
        if (selectedImages.length > 5) {

            const prevStartIndex = webStartGalleryIndex === 0 ? webStartGalleryIndex : webStartGalleryIndex - 1;
            const prevEndIndex = webEndGalleryIndex === 5 ? webEndGalleryIndex : webEndGalleryIndex - 1;
            setWebStartGalleryIndex(prevStartIndex);
            setWebEndGalleryIndex(prevEndIndex);
        }

    };

    const handleCancel = () => {
        setAlertOpen(false);
        setAlertOn(false)
    };

    const renderImages = (start, end) => {
        return selectedImages.slice(start, end).map((image, index) => (
            <div key={index} className="imageContainer">
                <button
                    className="trashBtn"
                    onClick={() => webHandleTrashClick(index + start)}
                    style={{backgroundImage: `url(${trashIcon})`, backgroundSize: 'contain'}}
                >
                </button>
                <img
                    className="imageDisplay"
                    src={URL.createObjectURL(image)}
                    alt={`Image ${index + 1}`}
                />
            </div>
        ));
    };

    const handleAddMorePhotosClick = () => {
        if (selectedImages.length >= 10) {
            setMaxImagesAlert(true)
        } else {
            openFileFunc();
        }
    };

    const renderErrorAlert = (condition, title, text, onConfirm) => {
        if (condition) {
            //document.body.classList.add('modal-open');
            setAlertOn(true)
            return (
                <ErrorAlert
                    onConfirm={onConfirm}
                    title={title}
                    text={text}
                    confirmText={t("ok")}
                />
            );
        }
        return null;
    };

    return (
        <>
            {isMobile ? (
                <>
                    <h2 className="titleText baseText" style={{margin: 0}}>{t("readyToSend")}</h2>
                    <div className="galleryContainer"
                         onTouchStart={handleTouchStart}
                         onTouchEnd={handleTouchEnd}>
                        <NavigationButton onClick={prevImage} imageUrl={getPrevArrow()}/>
                        <div className="imageContainer">
                            <button className="trashBtn" onClick={handleTrashClick}
                                    style={{backgroundImage: `url(${trashIcon})`, backgroundSize: 'contain'}}>
                            </button>
                            <img className="imageDisplay"
                                 src={URL.createObjectURL(selectedImages[currentIndex])}
                                 alt={`Image ${currentIndex + 1}`}
                            />
                        </div>
                        <NavigationButton onClick={nextImage} imageUrl={getNextArrow()} isNext={true}/>
                    </div>
                    <h3 className="addMoreText baseText" onClick={handleAddMorePhotosClick}>{t("addMorePhotos")}</h3>
                    <Button additionalClasses="sendBtn" onClick={handleSendImages}>{t("sendToElliq")}</Button>
                    {isAlertOpen &&
                        <ConfirmAlert onConfirm={() => handleConfirm()}
                                      onCancel={handleCancel}
                                      title={alertTitle}
                                      text={alertText}
                                      confirmText={t("delete")}
                                      cancelText={t("cancel")}/>
                    }
                    {renderErrorAlert(imageToBig, t("errorTitle"), t("tooLargeBody"), handleConfirmError)}
                    {renderErrorAlert(maxImagesAlert, t("errorTitle"), t("maxImages"), handleImageMaxAlert)}
                </>
            ) : (
                <>
                    <div className="webGalleryBg">
                        <h2 className="titleText baseText" style={{margin: 0}}>{t("readyToSend")}</h2>
                        <div className={"rowPicturesContainerWeb"}>
                            {webPictureScrolling && (<NavigationButton onClick={prevImageWeb} imageUrl={getPrevArrowWeb()}/>)}
                            {renderImages(webStartGalleryIndex, webEndGalleryIndex)}
                            {webPictureScrolling && (<NavigationButton onClick={nextImageWeb} imageUrl={getNextArrowWeb()} isNext={true}/>)}
                        </div>
                        <h3 className="addMoreText baseText" onClick={handleAddMorePhotosClick}>{t("addMorePhotos")}</h3>
                        <Button additionalClasses="sendBtn" onClick={handleSendImages}>{t("sendToElliq")}</Button>
                    </div>
                    {isAlertOpen &&
                        <ConfirmAlert onConfirm={() => handleConfirmWeb(selectedDeletedIndex)}
                                      onCancel={handleCancel}
                                      title={alertTitle}
                                      text={alertText}
                                      confirmText={t("delete")}
                                      cancelText={t("cancel")}/>
                    }
                    {renderErrorAlert(imageToBig, t("errorTitle"), t("tooLargeBody"), handleConfirmError)}
                    {renderErrorAlert(maxImagesAlert, t("errorTitle"), t("maxImages"), handleImageMaxAlert)}
                </>
            )}
            <Toaster toastOptions={{className: 'baseText'}}/>
        </>
    );
}

export default ShowSelected;